import type { ComponentProps } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';

import { Tooltip } from '~/components';
import { cn } from '~/lib/cn';

interface TruncateWithTooltipProps {
  children: React.ReactNode;
  maxCharacters?: number;
  tooltipPosition?: ComponentProps<typeof Tooltip>['side'];
  className?: string;
}

export function TruncateWithTooltip({
  children,
  maxCharacters,
  tooltipPosition = 'top',
  className,
}: TruncateWithTooltipProps) {
  const divRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const divClassName = cn(
    'min-w-0 whitespace-nowrap text-start',
    isOverflowing ? 'truncate' : '',
    className,
  );

  const contentStyle = {
    maxWidth: maxCharacters ? `${maxCharacters}ch` : '100%',
  };

  useLayoutEffect(() => {
    const checkOverflow = () => {
      if (divRef.current) {
        const { scrollWidth, clientWidth } = divRef.current;

        setIsOverflowing(scrollWidth > clientWidth);
      }
    };

    checkOverflow();

    window.addEventListener('resize', checkOverflow);

    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, [children]);

  return (
    <Tooltip
      content={children}
      delayDuration={0}
      enabled={isOverflowing}
      side={tooltipPosition}
      contentClassName="cursor-pointer break-all"
      triggerAsChild
    >
      <div className={divClassName} style={contentStyle} ref={divRef}>
        {children}
      </div>
    </Tooltip>
  );
}

TruncateWithTooltip.displayName = 'TruncateWithTooltip';
